import { LitElement, html, css } from '@hh/global.js'

class HHInputWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: { reflect: true },
    filled: { reflect: true },
    label: {},
    type: {},
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.$input = this.querySelector(`[slot='input']`)
    this.$select = this.querySelector(`select[slot='input']`)

    if (this.$select) {
      this.preselectSelectValue()
    }

    const isDate = this.type === 'date'
    const isSafari = /^((?!chrome|chromium|android).)*safari/i.test(navigator.userAgent)

    if (isDate) {
      this.filled = true
      this.focused = true
    }

    if (!isSafari && this.$input && this.type === 'date' && this.$input) {
      this.addEventListener('click', () => {
        this.$input.showPicker?.() || this.$input.focus()
      })
    }

    if (this.$input?.tagName === 'TEXTAREA') {
      this.$input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault()
      })

      this.$input.addEventListener('input', (e) => {
        const cursor = e.target.selectionStart
        const cleaned = e.target.value.replace(/[\r\n]+/g, ' ')
        if (cleaned !== e.target.value) {
          e.target.value = cleaned
          e.target.setSelectionRange(cursor, cursor)
        }
      })
    }

    this._observeValueChanges()
    this._updateFilledState()
  }

  _observeValueChanges() {
    const input = this.querySelector('[slot="input"]')
    if (!input) return

    this._observer = new MutationObserver(() => this._updateFilledState())
    this._observer.observe(input, {
      attributes: true,
      attributeFilter: ['value'],
    })

    const nativeDescriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input), 'value')
    if (nativeDescriptor?.configurable) {
      Object.defineProperty(input, 'value', {
        get: () => nativeDescriptor.get.call(input),
        set: (val) => {
          nativeDescriptor.set.call(input, val)
          this._updateFilledState()
        },
      })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._observer?.disconnect()
  }

  _updateFilledState() {
    const input = this.querySelector('[slot="input"]')
    if (input) this.filled = !!input.value
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default
    }
  }

  onChange(e) {
    if (this.animation === 'none' || this.label === 'stacked') {
      return
    }
    this.filled = !!e.target.value
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = true
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return
    }
    if (this.type !== 'date') {
      this.focused = false
    }
  }

  removeFocus() {
    this.focused = false
    this.filled = false
  }

  removeFocus() {
    this.focused = false
    this.filled = false 
  }

  render() {
    return html`
      <slot name="label"></slot>
      <slot name="icon"></slot>
      <slot
        name="input"
        @change="${this.onChange}"
        @keydown="${this.onChange}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
      ></slot>
    `
  }

  static styles = css`
    ::slotted(label) {
      position: absolute;
      color: var(--hh-color-grey) !important;
      height: var(--hh-height, 45px);
      font-size: 0.7500rem !important;
      line-height: var(--hh-height, 45px) !important;
      padding-left: var(--hh-left-indent-small, 10px) !important;
      transform: translateY(0);
      transition: transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
    }

    @media screen and (min-width: 990px) {
      font-size: 0.8750rem !important;
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      width: 100%;
      height: var(--hh-height, 45px);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      z-index: 2;
      text-indent: var(--hh-left-indent-small, 10px);
      font: var(--hh-font) !important;
      height: auto;
      padding: 0.9375rem 0 0.3125rem !important;
      color: currentColor !important;
      background: transparent !important;
      border: 0 !important;
    }

    ::slotted(textarea) {
      box-sizing: border-box !important;
      margin-top: 1.8rem;
      padding-top: 0;
      resize: none;
    }

    ::slotted(input[type='date']) {
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background: transparent;
      z-index: 2;
      text-align: left;
      box-sizing: border-box !important;
      direction: ltr;
      text-indent: 0.3125rem;
    }
    
    ::slotted(input[type='date'])::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 2.5rem;
      background-color: var(--hh-background, #fff);
      pointer-events: none;
      z-index: 4;
    }

    ::slotted(input) {
      line-height: 2.6250rem !important;
      box-sizing: border-box !important;
    }

    ::slotted(select) {
      appearance: none;
    }

    :host([focused='true']) ::slotted(label),
    :host([filled='true']) ::slotted(label) {
      font-size: var(--hh-minified-font-size, 9px) !important;
      transform: translateY(-13px);
    }

    :host([animation='disappear'][focused='true']) ::slotted(label),
    :host([animation='disappear'][filled='true']) ::slotted(label) {
      visibility: hidden;
      transform: translateY(0);
      font-size: inherit !important;
    }

    :host {
      position: relative;
      display: block;
      height: calc(var(--hh-height, 45px));
    }

    :host {
      background: transparent;
      border: var(--hh-border-size, 1px) solid currentColor;
      border-width: 1px !important;
    }

    :host([theme='minimal']) {
      background: transparent !important;
      border: none !important;
      border-bottom: var(--hh-border-size, 1px) solid currentColor !important;
    }

    :host([label='stacked']) {
      border: none;
      height: auto;
    }

    :host([label='stacked']) ::slotted(label),
    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      position: relative;
    }

    :host([label='stacked']) ::slotted(label) {
      height: auto;
      display: block;
      font: var(--hh-label-font) !important;
      margin: var(--hh-label-margin) !important;
      padding-left: 0 !important;
    }

    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      border: var(--hh-border-size, 1px) solid currentColor !important;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--hh-left-indent, 32px) !important;
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--hh-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--hh-right-indent, 32px) !important;
    }

    ::slotted([slot='icon']) {
      display: flex;
      position: absolute;
      width: var(--hh-icon-width, 23px);
      height: var(--hh-height, 45px);
      z-index: 3;
    }

    :host([icon='right']) ::slotted([slot='icon']) {
      right: var(--hh-icon-right, 10px);
    }

    :host([height='multiline']),
    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='compact']),
    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--hh-compact-height, 35px);
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--hh-compact-height, 35px) !important;
    }
  `
}

customElements.define('hh-input-wrapper', HHInputWrapper)
